import { Model } from '@vuex-orm/core'

import Checkout from '@/models/user/Checkout'
import HotelSearch from '@/models/hotels/HotelSearch'

import AppState from '@/utils/app.state'
import { getCode } from '@/lang'
import { Request } from '@vuex-orm/plugin-axios'

export default class PhysicalRoom extends Model {
  declare id: number
  declare roomIDs: string[]
  declare physicalRoomID: string
  declare configurationId: string
  declare number: number
  declare checkout: Checkout
  declare buildingName?: string
  declare floorName?: string

  static entity = 'physicalRoom'
  static primaryKey = 'id'

  static fields() {
    return {
      id: this.number(null),
      roomIDs: this.attr(null),
      physicalRoomID: this.attr(null),
      configurationId: this.attr(null),
      number: this.number(null),
      checkout: this.belongsTo(Checkout, 'selectedPhysicalRoomId'),
      buildingName: this.attr(null),
      floorName: this.attr(null)
    }
  }

  static apiConfig = {
    actions: {
      fetch(this: Request) {
        return this.post(
          '/content/v2/rooms/getphysicalroomsbypropertyids',
          {
            propertyIDs: [AppState.currentProperty],
            languageCodes: [getCode()]
          }
        )
      },

      /**
       * Get the available physical room IDs from the room-rate ID
       * @param {Number} roomID The room-rate ID
       * @returns {Array<Number>} The available physical room IDs
       */
      async fetchByRoomID(this: Request, roomID: string) {
        /* Find physical rooms which belong to the given room ID */
        const physicalRooms = PhysicalRoom.query()
          .where('roomIDs', (roomIDs: string[]) => roomIDs.includes(roomID))
          .get()

        /* Variable for storing the API response */
        let response

        try {
          /* Make the API call */
          response = await this.post(
            `/accommodation/v1/properties/${AppState.currentProperty}/pms/getavailableroomcategories`,
            {
              dateFrom: HotelSearch.instance.startDate,
              dateTo: HotelSearch.instance.endDate,
              physicalRoomIDs: physicalRooms.map(room => room.id),
            },
            { save: false }
          )

          /* Extract the response */
          response = response.response.data.availableRoomCategories

          /* Check the response */
          if (!Array.isArray(response)) {
            throw TypeError(
              `Invalid response: getavailableroomcategories.\
              Expected Array, got ${typeof response}`
            )
          }
        } catch (e) {
          console.error(e)
          return []
        }

        /* Get the room from the response if present */
        const roomResponse = response.find(it => it.roomCategoryID === roomID)
        if (!roomResponse) {
          return []
        }

        /* Return an intersection */
        const availablePhysicalRoomIDs = new Set(roomResponse.availablePhysicalRoomIDs)
        return physicalRooms.filter(physicalRoom => availablePhysicalRoomIDs.has(physicalRoom.id))
      }
    }
  }
}
